import { clsx } from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { IconButton } from '../../components/ui';
import { DEFAULT_LOCALE } from '../../lib/format';
import { type WeekStart, addDays, addMonths, formatDateParam, isSameDay, startOfWeek } from './model';

const MONTH_TITLE = new Intl.DateTimeFormat(DEFAULT_LOCALE, { month: 'long', year: 'numeric' });
const NARROW_DAY = new Intl.DateTimeFormat(DEFAULT_LOCALE, { weekday: 'narrow' });
const LONG_DAY = new Intl.DateTimeFormat(DEFAULT_LOCALE, { weekday: 'long' });
const FULL_DATE = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
});

export type MiniMonthProps = {
  /** The calendar's date; its month is shown first. */
  date: Date;
  /** Days the main view shows (day/week), shaded; null shades nothing. */
  range: { start: Date; end: Date } | null;
  weekStartsOn: WeekStart;
  now: Date;
  onSelect: (day: Date) => void;
};

/**
 * Month picker in the sidebar. One day is in the tab order; arrow keys move by day/week, Page Up/Down by month,
 * Home/End to the week's ends, Enter picks. Remount it (key) when the calendar's month changes.
 */
export function MiniMonth({ date, range, weekStartsOn, now, onSelect }: MiniMonthProps) {
  const [month, setMonth] = useState(() => new Date(date.getFullYear(), date.getMonth(), 1));
  const [focused, setFocused] = useState(date);
  const gridRef = useRef<HTMLDivElement>(null);
  const days = Array.from({ length: 42 }, (_, i) => addDays(startOfWeek(month, weekStartsOn), i));
  const weeks = Array.from({ length: 6 }, (_, i) => days.slice(i * 7, i * 7 + 7));
  const focusable =
    focused.getMonth() === month.getMonth() && focused.getFullYear() === month.getFullYear()
      ? focused
      : month;

  // Keyboard moves keep focus on the day they move to (only while the grid already has focus).
  useEffect(() => {
    const grid = gridRef.current;
    if (grid?.contains(document.activeElement))
      grid.querySelector<HTMLElement>(`[data-day="${formatDateParam(focused)}"]`)?.focus();
  }, [focused, month]);

  const moveFocus = (next: Date) => {
    setFocused(next);
    if (next.getMonth() !== month.getMonth() || next.getFullYear() !== month.getFullYear()) {
      setMonth(new Date(next.getFullYear(), next.getMonth(), 1));
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const offset = (days.findIndex((d) => isSameDay(d, focused)) + 7) % 7;
    const moves: Record<string, () => Date> = {
      ArrowLeft: () => addDays(focused, -1),
      ArrowRight: () => addDays(focused, 1),
      ArrowUp: () => addDays(focused, -7),
      ArrowDown: () => addDays(focused, 7),
      PageUp: () => addMonths(focused, -1),
      PageDown: () => addMonths(focused, 1),
      Home: () => addDays(focused, -offset),
      End: () => addDays(focused, 6 - offset),
    };
    const move = moves[event.key];
    if (!move) return;
    event.preventDefault();
    moveFocus(move());
  };

  const shiftMonth = (delta: number) => {
    const next = addMonths(month, delta);
    setMonth(next);
    setFocused(next);
  };

  return (
    <div className="select-none">
      <div className="mb-1 flex items-center justify-between">
        <span className="pl-1 text-[13px] font-semibold text-slate-900">{MONTH_TITLE.format(month)}</span>
        <span className="flex">
          <IconButton icon={ChevronLeft} label="Previous month" size="sm" onClick={() => shiftMonth(-1)} />
          <IconButton icon={ChevronRight} label="Next month" size="sm" onClick={() => shiftMonth(1)} />
        </span>
      </div>
      <div
        ref={gridRef}
        role="grid"
        aria-label={MONTH_TITLE.format(month)}
        onKeyDown={onKeyDown}
        className="text-center text-2xs"
      >
        <div role="row" className="grid grid-cols-7">
          {weeks[0]?.map((day) => (
            <span
              key={day.getDay()}
              role="columnheader"
              aria-label={LONG_DAY.format(day)}
              className="py-1 font-medium text-slate-400"
            >
              {NARROW_DAY.format(day)}
            </span>
          ))}
        </div>
        {weeks.map((week) => (
          <div key={week[0]?.getTime()} role="row" className="grid grid-cols-7">
            {week.map((day) => {
              const inRange = range !== null && day >= range.start && day < range.end;
              const today = isSameDay(day, now);
              const selected = isSameDay(day, date);
              return (
                <button
                  key={day.getTime()}
                  type="button"
                  role="gridcell"
                  data-day={formatDateParam(day)}
                  aria-label={FULL_DATE.format(day)}
                  aria-selected={inRange || selected}
                  aria-current={today ? 'date' : undefined}
                  tabIndex={isSameDay(day, focusable) ? 0 : -1}
                  onClick={() => {
                    setFocused(day);
                    onSelect(day);
                  }}
                  className={clsx(
                    'flex h-7 items-center justify-center',
                    inRange && 'bg-slate-100 first:rounded-l-md last:rounded-r-md',
                    inRange && range && isSameDay(day, range.start) && 'rounded-l-md',
                    inRange && range && isSameDay(addDays(day, 1), range.end) && 'rounded-r-md',
                  )}
                >
                  <span
                    className={clsx(
                      'flex size-6 items-center justify-center rounded-full tabular',
                      today
                        ? 'bg-ember-500 font-semibold text-white'
                        : selected
                          ? 'bg-ember-100 font-semibold text-ember-800'
                          : day.getMonth() === month.getMonth()
                            ? 'text-slate-700 hover:bg-slate-200'
                            : 'text-slate-400 hover:bg-slate-200',
                    )}
                  >
                    {day.getDate()}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
